import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag, Linkedin, Facebook, Link as LinkIcon, MessageCircle, Share2, Sparkles } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import NewsletterSignup from "@/components/NewsletterSignup";
import AnswerFirst from "@/components/seo/AnswerFirst";
import { toast } from "sonner";
import { BlogBody } from "@/components/blog/BlogBody";
import { CompanySpotlight } from "@/components/blog/CompanySpotlight";

/* eslint-disable react/no-unescaped-entities */

// Convert a simple Markdown body into safe HTML. Supports H2/H3, ordered
// and unordered lists, bold/italic, links, and paragraphs. We intentionally
// avoid raw-HTML injection and `dangerouslySetInnerHTML`-with-user-content
// by sanitizing every line — the source is our own LLM, but defense in
// depth never hurts.
function mdToHtml(md) {
  if (!md) return "";
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => s
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');
  const lines = md.split(/\r?\n/);
  let html = "";
  let buf = [];
  let listType = null;
  const flushList = () => {
    if (listType) {
      html += `<${listType}>` + buf.map((b) => `<li>${inline(b)}</li>`).join("") + `</${listType}>`;
      buf = []; listType = null;
    }
  };
  const flushPara = () => {
    if (buf.length) { html += "<p>" + inline(buf.join(" ")) + "</p>"; buf = []; }
  };
  for (const raw of lines) {
    const line = escape(raw);
    if (/^\s*$/.test(line)) { flushList(); flushPara(); continue; }
    const h2 = line.match(/^##\s+(.*)$/);
    const h3 = line.match(/^###\s+(.*)$/);
    const ul = line.match(/^\s*[-*]\s+(.*)$/);
    const ol = line.match(/^\s*\d+\.\s+(.*)$/);
    if (h2) { flushList(); flushPara(); html += `<h2>${inline(h2[1])}</h2>`; }
    else if (h3) { flushList(); flushPara(); html += `<h3>${inline(h3[1])}</h3>`; }
    else if (ul) { flushPara(); if (listType !== "ul") { flushList(); listType = "ul"; } buf.push(ul[1]); }
    else if (ol) { flushPara(); if (listType !== "ol") { flushList(); listType = "ol"; } buf.push(ol[1]); }
    else { flushList(); buf.push(line); }
  }
  flushList();
  flushPara();
  return html;
}

// Hand-crafted seed posts that always appear (in addition to the auto-
import { LESSON_POSTS } from "@/pages/blog-lessons";
import { useBlogFeed, blogUrl } from "@/lib/blog-feed";

// generated ones from the backend). Edit/extend this array to feature new
// editorial pieces.
// Fallback image pool — used to break up the "same photo twice in a row"
// duplication users kept flagging. Deterministic per-post pick from the
// slug so the image stays stable but neighbours don't clash.
const _FALLBACK_HEROES = [
  "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=70",
  "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=1200&q=70",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=70",
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=70",
  "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&w=1200&q=70",
  "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1200&q=70",
];
function _pickHero(slug, incoming) {
  if (incoming) return incoming;
  let h = 0;
  const s = String(slug || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return _FALLBACK_HEROES[h % _FALLBACK_HEROES.length];
}
// De-dup consecutive images so the "Latest for Ontario" strip never shows
// the same hero photo twice in a row. Also runs on the main list.
function _diversifyHeroes(posts) {
  const out = [];
  let last = null;
  for (const p of posts || []) {
    let img = _pickHero(p.slug, p.hero_image);
    if (img === last) {
      // Rotate deterministically to the next slot.
      let idx = _FALLBACK_HEROES.indexOf(img);
      if (idx < 0) idx = 0;
      img = _FALLBACK_HEROES[(idx + 1) % _FALLBACK_HEROES.length];
    }
    last = img;
    out.push({ ...p, hero_image: img });
  }
  return out;
}

const HAND_WRITTEN_POSTS = [
  {
    slug: "t776-form-canadian-landlords-guide",
    title: "T776 for Canadian Landlords: The Complete 2026 Filing Guide",
    excerpt: "Every Canadian landlord with a rental property must file a T776 with their personal tax return. Here's exactly how to fill it out — line by line — with real examples.",
    category: "Taxes",
    read_mins: 8,
    published_at: "2026-02-12T09:00:00Z",
    keyword: "T776 form for Canadian landlords",
    hero_image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "T776 Statement of Real Estate Rentals — Canadian landlord tax filing guide",
    Body: T776Post,
  },
  {
    slug: "alberta-security-deposit-interest-calculator-2026",
    title: "Alberta Security Deposit Interest: 2026 Rate, Math, and Calculator",
    excerpt: "Alberta law requires landlords to pay annual interest on every security deposit. Get the 2026 rate, the formula, and a copy-paste spreadsheet calculator.",
    category: "Alberta",
    read_mins: 6,
    published_at: "2026-02-05T09:00:00Z",
    keyword: "Alberta security deposit interest 2026",
    hero_image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "Alberta security deposit interest calculator 2026 — rental house guide",
    Body: AlbertaDepositPost,
  },
  {
    slug: "n4-notice-ontario-how-to-fill-out",
    title: "How to Fill Out an N4 Notice in Ontario (Without Losing at the LTB)",
    excerpt: "Most N4 notices for unpaid rent get tossed at the Landlord and Tenant Board because of a small clerical mistake. Avoid the 7 most common errors.",
    category: "Ontario",
    read_mins: 7,
    published_at: "2026-01-28T09:00:00Z",
    keyword: "How to fill out N4 notice Ontario",
    hero_image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "N4 notice Ontario Landlord and Tenant Board filing guide",
    Body: N4Post,
  },
  {
    slug: "kijiji-vs-central-rentals-canada",
    title: "Kijiji vs Central Rentals Canada: Which Should Canadian Landlords Use in 2026?",
    excerpt: "Kijiji is free and gets you views. Central Rentals collects rent, screens tenants, exports your T776, and never lets a deposit go uncollected. Here's an honest, side-by-side breakdown.",
    category: "Compare",
    read_mins: 9,
    published_at: "2026-02-20T09:00:00Z",
    keyword: "Kijiji vs Central Rentals Canada",
    hero_image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "Kijiji vs Central Rentals Canada — Canadian landlord software comparison 2026",
    Body: KijijiVsCentralRentalsPost,
  },
  {
    slug: "best-property-management-software-canada-2026",
    title: "Best Property Management Software in Canada 2026 (Honest Ranking)",
    excerpt: "We evaluated 12 property management platforms against Canadian rules — T776 export, RTA/RTB templates, CAD-native rent collection, French support. Here's the top 7 for 2026.",
    category: "Compare",
    read_mins: 11,
    published_at: "2026-02-24T09:00:00Z",
    keyword: "best property management software canada 2026",
    hero_image: "https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "Best property management software Canada 2026 ranked comparison landlord software",
    Body: BestPmsCanadaPost,
  },
  {
    slug: "cheapest-property-management-software-canada",
    title: "Cheapest Property Management Software in Canada for Small Landlords (2026)",
    excerpt: "You don't need $200/month AppFolio to run 4 rentals. Here's every property-management platform in Canada that costs under $50/month, ranked by what you actually get for the money.",
    category: "Pricing",
    read_mins: 8,
    published_at: "2026-02-25T09:00:00Z",
    keyword: "cheapest property management software canada",
    hero_image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "Cheapest property management software Canada under $50 per month for small landlords",
    Body: CheapestPmsPost,
  },
  {
    slug: "buildium-alternatives-canadian-landlords",
    title: "7 Buildium Alternatives Built for Canadian Landlords (2026)",
    excerpt: "Buildium is US-first — no T776, no e-Transfer, no Loi 41 lease. These 7 platforms are Canadian-built and typically cost less. Feature-by-feature comparison inside.",
    category: "Compare",
    read_mins: 10,
    published_at: "2026-02-26T09:00:00Z",
    keyword: "buildium alternatives canadian landlords",
    hero_image: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "Buildium alternatives for Canadian landlords 2026 comparison chart",
    Body: BuildiumAlternativesPost,
  },
  {
    slug: "alberta-small-landlord-starter-guide-2026",
    title: "Alberta Small-Landlord Starter Guide (1–5 units, 2026)",
    excerpt: "Everything a 1–5 unit Alberta landlord needs: registering, security deposits with 2026 interest, RTDRS non-payment path, and Line 8710 tricks for T776. Free templates inside.",
    category: "Alberta",
    read_mins: 9,
    published_at: "2026-02-27T09:00:00Z",
    keyword: "small landlord software alberta",
    hero_image: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "Alberta small landlord starter guide 2026 — Calgary Edmonton rental setup",
    Body: AlbertaSmallLandlordPost,
  },
  {
    slug: "canada-rent-increase-cap-2026-every-province",
    title: "Rent Increase Cap in Every Canadian Province (2026)",
    excerpt: "The 2026 rent-increase cap for all 13 provinces + territories, the required notice period, and the exact form you have to serve to make the raise legal.",
    category: "Provincial law",
    read_mins: 6,
    published_at: "2026-02-28T09:00:00Z",
    keyword: "rent increase cap canada 2026 by province",
    hero_image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "Rent increase cap Canada 2026 by province — Ontario BC Alberta Quebec landlord notice",
    Body: RentIncrease2026Post,
  },
  {
    slug: "how-to-raise-rent-ontario-2026-form-n1-guide",
    title: "How to Raise Rent in Ontario in 2026 (Form N1, 2.5% Cap Guide)",
    excerpt: "Ontario's 2026 rent-increase guideline is 2.5%. Here's exactly how to serve Form N1, the 90-day notice window, and the mistakes that make your raise void.",
    category: "Ontario",
    read_mins: 7,
    published_at: "2026-03-01T09:00:00Z",
    keyword: "how to raise rent ontario 2026",
    hero_image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "How to raise rent in Ontario 2026 — landlord serving Form N1 notice with 90 day window",
    Body: OntarioRentIncreasePost,
  },
  {
    slug: "commercial-electrical-pm-contract-playbook-2026",
    title: "The 2026 Commercial-Electrical PM Contract Playbook (Renew 94% of Your Contracts)",
    excerpt: "Recurring PM contracts are the highest-margin work in commercial electrical — and the easiest to lose. Here's the exact 6-part contract template, pricing formula, and 30-day renewal sequence used by shops running at 94% renewal.",
    category: "Trades",
    read_mins: 11,
    published_at: "2026-03-05T09:00:00Z",
    keyword: "commercial electrical pm contract template",
    hero_image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=1200&q=70",
    hero_alt: "Commercial electrical contractor with laptop reviewing preventive maintenance contract",
    Body: CommercialElectricalPmPost,
  },
];

// Concatenate the curated Landlord University lessons so every /blog/<slug>
// link from the University page resolves to a real post.
const SEED_POSTS = [...HAND_WRITTEN_POSTS, ...LESSON_POSTS];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
};

// ─── Index page ────────────────────────────────────────────────────────────
export default function Blog() {
  const { posts: autoPosts, loading, error, hasMore, loadMore, retry } = useBlogFeed();
  useEffect(() => { document.title = "Blog · Central Rentals Canada"; }, []);
  const allPosts = _diversifyHeroes(
    [...new Map([...SEED_POSTS, ...autoPosts].map((post) => [post.slug, post])).values()].sort((a, b) =>
      new Date(b.published_at || 0) - new Date(a.published_at || 0))
  );
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="blog-page">
      <PublicHeader light={true} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-20">
        {/* HERO CARD — Pocketpills style */}
        <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-b from-emerald-100 via-emerald-50 to-white p-6 sm:p-12">
          <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-emerald-200/60 blur-2xl"/>
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-emerald-200 px-3 py-1.5 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-800">
              <Tag className="w-3.5 h-3.5"/> Field notes · Canadian rental guides
            </div>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-slate-950">
              Field notes for<br className="hidden sm:block"/> <span className="text-emerald-600">Canadian landlords.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-700 leading-relaxed max-w-2xl">
              Tax tips, provincial law, and operational guides — written for landlords with 1 to 1,000 doors across Canada.
            </p>
            <div className="mt-8 max-w-xl">
              <NewsletterSignup source="blog" compact />
            </div>
          </div>
        </div>

        {error && <div role="alert" className="mt-6 flex flex-wrap items-center gap-3 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-950" data-testid="blog-feed-error"><p>{error} Existing guides remain available below.</p><button type="button" onClick={retry} disabled={loading} className="font-semibold underline" data-testid="blog-feed-retry">Retry</button></div>}
        {loading && <p role="status" className="mt-6 text-sm text-slate-600" data-testid="blog-feed-loading">Loading articles…</p>}
        {/* POST GRID — rounded pastel cards */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {allPosts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group block rounded-[28px] bg-white border border-emerald-100 hover:border-emerald-300 hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden"
            >
              {p.hero_image && (
                <div className="aspect-[16/10] bg-emerald-50 overflow-hidden">
                  <img src={p.hero_image} alt={p.hero_alt || p.title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                </div>
              )}
              <div className="p-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-800">
                  <Tag className="w-3 h-3"/> {p.category}
                </div>
                <h2 className="font-display mt-4 text-xl font-extrabold leading-tight text-slate-950 group-hover:text-emerald-700 transition-colors">
                  {p.title}
                </h2>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
                <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3"/> {fmtDate(p.published_at)}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/> {p.read_mins} min read</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        {hasMore && <div className="mt-8 text-center"><button type="button" onClick={loadMore} disabled={loading} className="rounded-md border border-emerald-700 px-6 py-3 font-semibold text-emerald-800 transition-colors hover:bg-emerald-50 disabled:opacity-50" data-testid="blog-load-more">{loading ? "Loading…" : "Load more articles"}</button></div>}
      </main>
    </div>
  );
}

// ─── Single post page ──────────────────────────────────────────────────────

// Per-category fallback FAQ — shown at the bottom of every blog post and
// emitted as FAQPage JSON-LD. Visible AND structured = Google rich result.
const CATEGORY_FAQ = {
  "Taxes": [
    { q: "What's the deadline to file rental income to the CRA?", a: "Rental income is reported on the T776 form filed with your personal T1 return. The deadline is April 30 of the year after you earned the income (June 15 if you're self-employed, but any balance owing is still due April 30)." },
    { q: "Do I need to charge GST/HST on rent?", a: "Long-term residential rent is GST/HST-exempt. Short-term rentals (under 30 days) are taxable once you exceed the $30,000 small-supplier threshold across all your business activities." },
    { q: "Can I deduct mortgage payments?", a: "You can deduct the interest portion (and most carrying costs) of your mortgage on a rental property, but NOT the principal repayment. Central Rentals splits this automatically inside your T776 export." },
  ],
  "Ontario": [
    { q: "How much can I raise rent in Ontario in 2026?", a: "The Government of Ontario publishes the rent increase guideline each June for the following calendar year. Most years it's between 1.5% and 2.5%. Central Rentals tracks the live figure inside the rent increase tool." },
    { q: "How do I evict a tenant in Ontario?", a: "Serve the right N-form (N4 for unpaid rent, N12 for owner-use, etc.), wait the legal notice period, then file the corresponding L-form with the LTB. Never self-help evict — fines reach $50,000." },
    { q: "Is the Ontario Standard Lease mandatory?", a: "Yes. All residential tenancies since April 30, 2018 must use the Ontario Standard Lease (Form 2229E). Skipping it gives the tenant a withholding right." },
  ],
  "Alberta": [
    { q: "Does Alberta have rent control?", a: "No. Alberta has no statutory rent cap — landlords can raise rent any amount on a periodic tenancy with 3 months' notice." },
    { q: "What's the Alberta security deposit interest rate?", a: "The rate is set annually by the Government of Alberta and applied to every deposit. Central Rentals auto-applies the published rate each January." },
    { q: "RTDRS or court?", a: "RTDRS for most disputes under $50,000 (fastest, cheapest). Court of King's Bench for complex legal questions or larger claims." },
  ],
  "British Columbia": [
    { q: "What's the BC rent increase cap?", a: "Set annually by the BC government each September. The 2025 cap is 3.0%. Notices must be served at least 3 months before the increase and only once per 12 months." },
    { q: "Do I have to use RTB forms?", a: "Yes. The Residential Tenancy Act requires landlords to use the RTB's official forms for notices, increases, and applications. Custom forms are void." },
    { q: "How long does an RTB hearing take?", a: "Direct Request (unpaid rent) decisions: 2–4 weeks. Participatory hearings: 3–5 months." },
  ],
  "Quebec": [
    { q: "How is rent increase calculated in Quebec?", a: "The TAL publishes an official calculation grid each January. Central Rentals applies the grid (operating costs, taxes, capital improvements) to estimate the increase that will survive a TAL challenge." },
    { q: "Is the Form F lease mandatory in Quebec?", a: "Yes. Only the Form F (bail) issued by the TAL is a valid residential lease in Quebec. Side agreements must be attached as an addendum." },
    { q: "How long does TAL take for non-payment?", a: "Target is 1.5 months. Real-world averages in 2024–2025 are 12–24 months due to backlog." },
  ],
  "Investing": [
    { q: "What's a good cap rate in Canada?", a: "Major urban markets (Toronto, Vancouver) sit at 3–4%. Secondary markets (Calgary, Halifax, Winnipeg) trend 5–7%. Tertiary markets can reach 8%+. Compare to the 10-year GoC bond + a 3–4% risk premium as a sanity check." },
    { q: "Should I incorporate my rental property?", a: "Usually no for under 3 doors — the cost of incorporation outweighs the tax benefit. For 4+ doors or short-term rentals operating as a business, a corporation often pays for itself by year 2. Talk to a CPA." },
  ],
  "Screening": [
    { q: "Can I run a credit check on a Canadian tenant?", a: "Yes, with the applicant's written consent. Equifax and TransUnion both have landlord-tier reports, or you can use a screening partner like Certn." },
    { q: "What can I legally ask on a rental application?", a: "Income, employment, rental history, references. You cannot ask about race, religion, family status, disability, or any other ground protected under provincial human rights codes." },
  ],
};

function PostFAQ({ post }) {
  if (!post) return null;
  // Prefer AEO-generated FAQ (tailored to THIS post's content) over the static
  // category fallback — both crawlers and humans see the more relevant questions.
  const aeo = post.aeo && Array.isArray(post.aeo.faq) ? post.aeo.faq : null;
  const isAeo = !!(aeo && aeo.length);
  const list = isAeo
    ? aeo.map((f) => ({ q: f.question, a: f.answer }))
    : (CATEGORY_FAQ[post.category] || CATEGORY_FAQ["Taxes"]);
  return (
    <section className="mt-12 pt-9 border-t border-slate-200 not-prose" data-testid="blog-faq">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-3 flex items-center gap-2">
        Frequently asked
        {isAeo && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700"
            title="Generated by AEO engine — eligible for search Overviews / ChatGPT / Perplexity"
            data-testid="blog-aeo-faq-badge">
            <Sparkles className="w-3 h-3"/> AEO
          </span>
        )}
      </div>
      <h2 className="font-display text-xl md:text-2xl font-bold text-slate-900 mb-5">Common questions</h2>
      <div className="space-y-2.5">
        {list.map((f, i) => (
          <details key={i} className="group p-4 rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors" data-testid={`blog-faq-item-${i}`}>
            <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-700 text-xs flex items-center justify-center font-bold mt-0.5 flex-none">Q</span>
              <span className="flex-1">{f.q}</span>
            </summary>
            <p className="mt-3 ml-8 text-sm text-slate-600 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function ShareRow({ post }) {
  if (!post) return null;
  const url = `https://centralrentals.ca/blog/${post.slug}`;
  const title = post.title || "";
  const enc = (s) => encodeURIComponent(s);
  const buttons = [
    { name: "LinkedIn", icon: Linkedin, color: "text-sky-700 hover:bg-sky-50 hover:border-sky-300", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { name: "Facebook", icon: Facebook, color: "text-blue-700 hover:bg-blue-50 hover:border-blue-300", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { name: "Reddit",   icon: MessageCircle, color: "text-orange-700 hover:bg-orange-50 hover:border-orange-300", href: `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(title)}` },
    { name: "Hacker News", icon: MessageCircle, color: "text-amber-700 hover:bg-amber-50 hover:border-amber-300", href: `https://news.ycombinator.com/submitlink?u=${enc(url)}&t=${enc(title)}` },
  ];

  // Mobile in-app browsers (LinkedIn, Facebook, previews pane)
  // sandbox `<a target="_blank">` so the sharer URLs go nowhere visible.
  // Fix: route every click through window.open(); if the popup is blocked
  // (typical inside iframe sandboxes) fall back to navigating top-window.
  const openShare = (href) => {
    try {
      const w = window.open(href, "_blank", "noopener,noreferrer,width=720,height=620");
      if (w && !w.closed) return;
    } catch { /* fall through */ }
    try { window.top.location.href = href; }
    catch { window.location.href = href; }
  };

  // Native share sheet — primary CTA on mobile + every in-app browser.
  // Web Share API is available in iOS Safari/Chrome and Android Chrome,
  // and crucially also inside LinkedIn/Facebook in-app browsers because
  // they expose `navigator.share` from the host OS.
  const hasNative = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const nativeShare = async () => {
    try {
      await navigator.share({ title, url, text: post?.excerpt || title });
    } catch { /* user cancelled */ }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy — long-press the URL to copy manually");
    }
  };
  return (
    <div className="mt-10 pt-7 border-t border-slate-200 not-prose" data-testid="blog-share">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">Share this post</div>
      <div className="flex flex-wrap gap-2">
        {hasNative && (
          <button type="button" onClick={nativeShare} data-testid="blog-share-native"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100 transition-colors">
            <Share2 className="w-3.5 h-3.5"/> Share…
          </button>
        )}
        {buttons.map((b) => {
          const Icon = b.icon;
          return (
            <button type="button" key={b.name} onClick={() => openShare(b.href)}
              data-testid={`blog-share-${b.name.toLowerCase().replace(" ", "-")}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold transition-colors ${b.color}`}>
              <Icon className="w-3.5 h-3.5"/> {b.name}
            </button>
          );
        })}
        <button type="button" onClick={copy} data-testid="blog-share-copy"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors">
          <LinkIcon className="w-3.5 h-3.5"/> Copy link
        </button>
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const seed = SEED_POSTS.find((p) => p.slug === slug);
  const [auto, setAuto] = useState(null);
  const [loading, setLoading] = useState(!seed);
  const [articleError, setArticleError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [related, setRelated] = useState([]);
  useEffect(() => {
    let cancelled = false;
    setLoading(!seed); setAuto(null); setArticleError("");
    (async () => {
      try {
        const r = await fetch(blogUrl(`posts/${encodeURIComponent(slug)}`), { cache: "no-store" });
        if (!r.ok && r.status !== 404) throw new Error("This article could not be loaded. Please retry.");
        const d = r.ok ? await r.json() : null;
        if (!cancelled) setAuto(d);
      } catch (error) {
        if (!cancelled) { setAuto(null); setArticleError(error.message); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, seed, retryCount]);
  // Pull a list of related posts for internal linking (PageRank distribution).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(blogUrl("posts"), { cache: "no-store" });
        const all = r.ok ? await r.json() : [];
        const list = (Array.isArray(all) ? all : []).filter((p) => p.slug && p.slug !== slug);
        const here = (seed || auto) || {};
        const sameCat = list.filter((p) => p.category && here.category && p.category === here.category);
        const other = list.filter((p) => !sameCat.includes(p));
        if (!cancelled) setRelated([...sameCat, ...other].slice(0, 3));
      } catch {
        if (!cancelled) setRelated([]);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, seed, auto]);
  const post = (auto?.slug === slug ? auto : null) || seed;
  // Per-post SEO: title, meta description, canonical, full OG/Twitter cards,
  // article:published_time, and JSON-LD BlogPosting schema. Removes the tags
  // on unmount so they don't leak into the next route.
  useEffect(() => {
    if (!post) return;
    const url = post.canonical_url || new URL(`/blog/${post.slug}`, process.env.REACT_APP_BACKEND_URL).href;
    const origin = new URL(url).origin;
    const desc = (post.meta_description || post.excerpt || "").slice(0, 280);
    const img = post.hero_image || new URL("/og-image.png", origin).href;

    document.title = `${post.title} · Central Rentals Canada`;

    // Helper — upsert a single meta tag and return its element.
    const setTag = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };
    const tags = [
      setTag('meta[name="description"]', { name: "description", content: desc }),
      setTag('link[rel="canonical"]', { rel: "canonical", href: url }),
      setTag('meta[property="og:type"]', { property: "og:type", content: "article" }),
      setTag('meta[property="og:title"]', { property: "og:title", content: post.title }),
      setTag('meta[property="og:description"]', { property: "og:description", content: desc }),
      setTag('meta[property="og:url"]', { property: "og:url", content: url }),
      setTag('meta[property="og:image"]', { property: "og:image", content: img }),
      setTag('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }),
      setTag('meta[name="twitter:title"]', { name: "twitter:title", content: post.title }),
      setTag('meta[name="twitter:description"]', { name: "twitter:description", content: desc }),
      setTag('meta[name="twitter:image"]', { name: "twitter:image", content: img }),
      setTag('meta[property="article:published_time"]', { property: "article:published_time", content: post.published_at || "" }),
      setTag('meta[property="article:modified_time"]', { property: "article:modified_time", content: post.updated_at || post.published_at || "" }),
      setTag('meta[property="article:section"]', { property: "article:section", content: post.category || "" }),
    ];

    // JSON-LD BlogPosting + FAQPage. AEO data is generated per-post during
    // publish (aeo_blog_service.py) and stored on post.aeo.faq — those Q&A
    // are tailored to the actual content of THIS post. We fall back to the
    // static CATEGORY_FAQ only when AEO didn't run (older posts, LLM offline).
    const aeoFaq = (post.aeo && Array.isArray(post.aeo.faq)) ? post.aeo.faq : null;
    const faqList = aeoFaq && aeoFaq.length
      ? aeoFaq.map((f) => ({ q: f.question || "", a: f.answer || "" }))
      : (CATEGORY_FAQ[post.category] || CATEGORY_FAQ["Taxes"]);
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "headline": post.title,
          "description": desc,
          "image": [{
            "@type": "ImageObject",
            "url": img,
            "width": 1200,
            "height": 630,
          }],
          "datePublished": post.published_at,
          "dateModified": post.updated_at || post.published_at,
          "author": { "@type": "Organization", "name": post.author || "Central Rentals Canada", "url": origin },
          "publisher": {
            "@type": "Organization",
            "name": "Central Rentals Canada",
            "logo": { "@type": "ImageObject", "url": new URL("/icon-512.png", origin).href }
          },
          "mainEntityOfPage": { "@type": "WebPage", "@id": url },
          "articleSection": post.category || undefined,
          "about": post.promotion ? { "@type": "Organization", "name": post.promotion.name, "url": post.promotion.website } : undefined,
          "wordCount": post.word_count || undefined,
        },
        {
          "@type": "FAQPage",
          "mainEntity": faqList.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        },
      ],
    };
    let script = document.head.querySelector('script[data-blog-jsonld="1"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-blog-jsonld", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);

    return () => {
      // Strip per-post tags on route change so the next page doesn't inherit them.
      tags.forEach((el) => { if (el && !el.hasAttribute("data-permanent")) el.remove(); });
      if (script) script.remove();
    };
  }, [post]);
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader light={true}/>
        <main className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-center text-slate-500">Loading…</main>
      </div>
    );
  }
  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader light={true}/>
        <main className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-center">
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="blog-article-unavailable">{articleError ? "Article temporarily unavailable" : "Post not found"}</h1>
          {articleError && <div className="mt-4" role="alert" data-testid="blog-article-error"><p>{articleError}</p><button type="button" onClick={() => setRetryCount((count) => count + 1)} className="mt-3 font-semibold text-emerald-800 underline" data-testid="blog-article-retry">Retry article</button></div>}
          <Link to="/blog" className="inline-flex items-center gap-2 mt-6 text-emerald-700 font-semibold hover:underline" data-testid="blog-unavailable-back">
            <ArrowLeft className="w-4 h-4"/> Back to all posts
          </Link>
        </main>
      </div>
    );
  }
  const Body = post.Body;
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`blog-post-${post.slug}`}>
      <PublicHeader light={true}/>
      <article className="max-w-3xl mx-auto px-5 md:px-8 pt-6 pb-20">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800 mb-6" data-testid="blog-back-link">
          <ArrowLeft className="w-4 h-4"/> All posts
        </Link>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700 inline-flex items-center gap-1.5 mb-3">
          <Tag className="w-3 h-3"/> {post.category}
        </div>
        <h1 className="font-display text-3xl md:text-5xl font-extrabold tracking-normal text-slate-900 leading-[1.08]" data-testid="blog-article-title">{post.title}</h1>
        {post.excerpt && (
          <AnswerFirst>{post.excerpt}</AnswerFirst>
        )}
        {post.promotion && <CompanySpotlight company={post.promotion} placement="top"/>}
        <div className="mt-4 flex items-center gap-4 text-sm text-slate-500 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5"/> {fmtDate(post.published_at)}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5"/> {post.read_mins} min read</span>
          {!post.promotion && post.aeo && Array.isArray(post.aeo.faq) && post.aeo.faq.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700"
              title="This post is optimized for search Overviews, ChatGPT Search, and Perplexity citations"
              data-testid="blog-aeo-badge">
              <Sparkles className="w-3 h-3"/> AEO optimized
            </span>
          )}
        </div>
        {post.hero_image && (
          <div className="mt-7 rounded-2xl overflow-hidden border border-slate-200" data-testid="blog-hero-image">
            <img src={post.hero_image} alt={post.hero_alt || post.title} loading="lazy" decoding="async" className="w-full aspect-[2/1] object-cover"/>
          </div>
        )}
        <div className="blog-content max-w-none mt-9 text-slate-700 leading-relaxed space-y-5">
          {Body ? <Body/> : <BlogBody html={post.html} markdown={post.body_md}/>}
        </div>

        {/* CTA at end of every post */}
        {post.promotion ? <CompanySpotlight company={post.promotion} placement="bottom"/> : <div className="mt-14 p-6 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-sky-50 not-prose">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Built for Canadian landlords</div>
          <h3 className="font-display text-xl md:text-2xl font-bold text-slate-900 mt-2">Tired of spreadsheets for rent, leases, and tax season?</h3>
          <p className="text-sm text-slate-600 mt-2 max-w-xl">Central Rentals handles T776 exports, provincial notices, Rent tracking, and tenant screening in one place. Free for 30 days.</p>
          <Link to="/signup?role=landlord&plan=professional" data-testid="blog-cta-signup"
            className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm shadow-sm">
            Choose a plan <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>}

        {/* One-click share — backlink seed; each share is a fresh signal to Google. */}
        <ShareRow post={post} />

        {/* Category-specific FAQ — visible AND emits FAQPage schema for rich-result eligibility. */}
        <PostFAQ post={post} />

        {related.length > 0 && (
          <section className="mt-16 pt-10 border-t border-slate-200 not-prose" data-testid="blog-related">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-5">Keep reading</div>
            <div className="grid sm:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`blog-related-${r.slug}`}
                  className="group block p-4 rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/40 transition-colors">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 mb-1.5">{r.category || "Insights"}</div>
                  <div className="font-display text-base font-bold text-slate-900 group-hover:text-emerald-800 leading-snug">{r.title}</div>
                  {r.read_mins && (
                    <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1.5"><Clock className="w-3 h-3"/>{r.read_mins} min read</div>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
}

// ─── Post 1: T776 ──────────────────────────────────────────────────────────
function T776Post() {
  return (
    <>
      <p>If you own a rental property in Canada and earned rental income last year, the Canada Revenue Agency expects you to file a <strong>T776 — Statement of Real Estate Rentals</strong> with your personal tax return. It is not optional, and the cost of getting it wrong ranges from a CRA reassessment to losing legitimate deductions you were entitled to claim.</p>
      <p>This guide walks through the T776 line by line, with examples drawn from real Canadian landlords managing one to ten doors. We will cover what counts as gross rental income, which expenses are deductible, how to handle co-owners, and the most common mistakes that cost landlords thousands at audit time.</p>

      <h2>Who must file a T776?</h2>
      <p>You file a T776 if you earned rental income from real estate in Canada during the tax year. That includes:</p>
      <ul>
        <li>A single-family home rented to a tenant for any portion of the year</li>
        <li>A condo or apartment unit you own and rent</li>
        <li>A basement suite or secondary suite in your principal residence</li>
        <li>Short-term rentals such as Airbnb (different rules apply for full-time short-term rentals — see below)</li>
        <li>Commercial property, parking spaces, or storage units rented out</li>
      </ul>
      <p>You file <em>one</em> T776 per rental operation, not one per property. If you own three rental houses, all three are reported on a single T776 with separate columns for each address.</p>

      <h2>Gross rental income — what counts</h2>
      <p>Gross rental income is every dollar you received from a tenant during the calendar year, including:</p>
      <ul>
        <li>Monthly rent payments</li>
        <li>Pet rent or parking add-ons</li>
        <li>Late fees and NSF cheque fees</li>
        <li>Lease break payments (yes, these are taxable income)</li>
        <li>Forfeited security deposits</li>
      </ul>
      <p><strong>What does NOT count:</strong> last month's rent held in trust (until the tenant moves out), and refundable security deposits while they are still in trust. These are liabilities on your books, not income.</p>

      <h2>The 12 deductible expense categories on Form T776</h2>
      <p>The CRA gives you 12 numbered expense lines plus an "Other" bucket. Use the actual line names — auditors flag mis-categorized expenses faster than missing ones.</p>
      <h3>1. Advertising</h3>
      <p>Kijiji boosts, Facebook Marketplace promotions, RentFaster listings, professional photography, signage. Yes, even the $40 you spent on a Canva Pro subscription used for listing flyers is deductible — pro-rated.</p>
      <h3>2. Insurance</h3>
      <p>Landlord insurance premiums only. If you have one combined policy covering personal home + rental, you must pro-rate.</p>
      <h3>3. Interest</h3>
      <p>Mortgage interest (NOT principal). Get your annual interest statement from your lender — it's usually emailed every January.</p>
      <h3>4. Maintenance and repairs</h3>
      <p>Fixing a broken furnace = deductible. Replacing the furnace = capital expense, depreciated via CCA (see below). The CRA distinguishes ruthlessly here — see the next section.</p>
      <h3>5. Management and administration fees</h3>
      <p>Property manager fees, software subscriptions (yes, including Central Rentals), accountant fees for preparing your T776, lawyer fees for lease drafting.</p>
      <h3>6. Motor vehicle expenses</h3>
      <p>Only if you have more than one rental property AND you drive to manage them. Track kilometers in a logbook — the CRA will ask for it.</p>
      <h3>7. Office expenses</h3>
      <p>Stationery, postage, printing, courier costs. Small bucket, easy to forget.</p>
      <h3>8. Legal, accounting, and other professional fees</h3>
      <p>Eviction lawyer fees, accountant fees specifically related to the rental (not your full personal return), Landlord and Tenant Board filing fees.</p>
      <h3>9. Property taxes</h3>
      <p>Annual municipal property taxes. If you live in the building too, pro-rate by square footage of the rental portion.</p>
      <h3>10. Salaries, wages, and benefits</h3>
      <p>Only if you employ a superintendent or onsite manager. Most small landlords leave this blank.</p>
      <h3>11. Travel</h3>
      <p>Different from motor vehicle. Use this for flights/hotels if your rental is out of province. Carefully documented or it will be challenged.</p>
      <h3>12. Utilities</h3>
      <p>Heat, water, electricity, internet — but only the portion you (the landlord) actually pay. If utilities are in the tenant's name, this is zero.</p>

      <h2>Capital expenses vs. current expenses — the trap that costs landlords thousands</h2>
      <p>This is the single most audited area on Form T776. The CRA splits every expense into two buckets:</p>
      <ul>
        <li><strong>Current expense:</strong> restores the property to its original state. Patching drywall, fixing a leaky tap, repainting one room. Fully deductible in the year incurred.</li>
        <li><strong>Capital expense:</strong> improves the property beyond its original state or extends its useful life. New roof, kitchen renovation, new windows. Deductible only via Capital Cost Allowance (CCA) over many years.</li>
      </ul>
      <p>Trying to claim a $14,000 kitchen reno as a "repair" is the fastest way to trigger a desk audit. When in doubt: improvements = capital, restorations = current.</p>

      <h2>Capital Cost Allowance (CCA) — the rules in plain English</h2>
      <p>CCA lets you depreciate the value of the building (NOT the land) over many years. For most residential rental buildings, the CCA class is <strong>Class 1 (4%)</strong> using the declining balance method.</p>
      <p><strong>Warning:</strong> claiming CCA on a property that may later become your principal residence (or vice versa) can trigger a deemed disposition with tax consequences. Talk to an accountant before claiming CCA the first time. Many small landlords intentionally do not claim CCA to preserve the principal residence exemption flexibility.</p>

      <h2>Co-ownership: filing as a co-owner vs. partner</h2>
      <p>If you own a property with a spouse or business partner, you each file your own T776 reporting only your share of income and expenses. For two equal co-owners on a $24,000-rent property, each person reports $12,000 gross.</p>
      <p>You're a <em>partnership</em> (different rules) only if you have a partnership agreement and operate as a business. Most spousal landlords are co-owners, not partners.</p>

      <h2>The 7 most common T776 mistakes</h2>
      <ol>
        <li><strong>Mixing personal and rental expenses.</strong> If you bought paint that was used for your own house and the rental, claim only the rental portion.</li>
        <li><strong>Forgetting late month rent.</strong> Rent received in January for December IS December income on accrual basis.</li>
        <li><strong>Claiming the whole mortgage payment.</strong> Only the interest is deductible. Principal payments build equity, not deductions.</li>
        <li><strong>No paper trail for cash receipts.</strong> If a tenant pays you e-Transfer, save the email. If they pay cash, write a receipt.</li>
        <li><strong>Claiming travel without a logbook.</strong> The CRA will deny vehicle deductions without one.</li>
        <li><strong>Forgetting to file when the property had a loss.</strong> You still file — and the loss can offset other income, subject to restrictions.</li>
        <li><strong>Not reporting roommate income.</strong> If you rent out a room in your home, you still owe a T776, even though some expenses are restricted.</li>
      </ol>

      <h2>Filing deadlines</h2>
      <p>T776 attaches to your personal T1 return. Standard deadlines:</p>
      <ul>
        <li><strong>April 30</strong> for most individuals</li>
        <li><strong>June 15</strong> if you or your spouse are self-employed (but any tax owed is still due April 30)</li>
      </ul>
      <p>If you have a tax liability, pay by April 30 even if your return is filed June 15.</p>

      <h2>Records you must keep</h2>
      <p>The CRA requires you to keep all rental records for <strong>six years</strong> from the end of the tax year they relate to. That includes:</p>
      <ul>
        <li>Lease agreements</li>
        <li>Rent receipts (every payment)</li>
        <li>Tenant communications about repairs</li>
        <li>Receipts and invoices for every claimed expense</li>
        <li>Mortgage interest statements</li>
        <li>Insurance policies</li>
      </ul>
      <p>Software that generates per-payment rent receipts and stores them in the cloud is no longer optional for serious landlords — it is the only practical way to survive a CRA audit five years after the fact.</p>

      <h2>One-line summary</h2>
      <p>Track every dollar in and every dollar out, distinguish current from capital, keep six years of receipts, and either claim CCA deliberately or skip it deliberately — never accidentally. Get those four right and the T776 takes 30 minutes a year.</p>
    </>
  );
}

// ─── Post 2: Alberta security deposit interest ────────────────────────────
function AlbertaDepositPost() {
  return (
    <>
      <p>Under section 44 of Alberta's <em>Residential Tenancies Act</em>, every landlord in the province must pay <strong>annual interest</strong> on a tenant's security deposit. Most landlords either forget entirely or pay the wrong rate — and at the end of the tenancy this turns into a small but real liability.</p>
      <p>This post gives you the current rate, the math, a copy-paste spreadsheet formula, and the documentation you need to satisfy the Residential Tenancy Dispute Resolution Service (RTDRS) if a former tenant ever disputes the math.</p>

      <h2>The 2026 Alberta security deposit interest rate</h2>
      <p>The rate is set by ministerial order and revised every January 1. For most of the past decade the rate has hovered between 0% and 1.5%. Always check Service Alberta's <em>Security Deposit Interest Rate</em> page before generating receipts — link below.</p>
      <p>Historical rates (annual, simple, payable on the deposit balance):</p>
      <ul>
        <li>2024: 1.5%</li>
        <li>2025: 1.5%</li>
        <li>2026: confirm at the official Service Alberta page (we update this post every January)</li>
      </ul>

      <h2>How the math works</h2>
      <p>Interest is calculated on the security deposit (and any pet damage deposit) for each tenancy year, compounded annually, payable at the end of each tenancy year or at lease end — whichever the tenant prefers.</p>
      <p><strong>Formula:</strong></p>
      <p><code>Year-end balance = Previous balance × (1 + annual rate)</code></p>

      <h3>Worked example</h3>
      <p>Tenant deposits $1,000 on Mar 1, 2024. The interest rate is 1.5% for 2024 and 1.5% for 2025.</p>
      <ul>
        <li>Mar 1, 2024 deposit: $1,000.00</li>
        <li>Mar 1, 2025 balance: $1,000 × 1.015 = $1,015.00 (interest earned: $15.00)</li>
        <li>Mar 1, 2026 balance: $1,015 × 1.015 = $1,030.23 (interest earned in year 2: $15.23)</li>
      </ul>
      <p>If the tenant moves out Mar 1, 2026 and you owe them the full deposit back, you write a cheque for $1,030.23 — not $1,000.</p>

      <h2>Copy-paste spreadsheet formula</h2>
      <p>In any spreadsheet (Excel, Google Sheets, Numbers), put the deposit in cell A1, the rate (as a decimal like 0.015) in B1, and the number of full years in C1:</p>
      <p><code>=A1 * POWER(1 + B1, C1)</code></p>
      <p>Adjust for partial years (move-out mid-year) using:</p>
      <p><code>=A1 * POWER(1 + B1, FullYears) + A1 * B1 * (PartialDays / 365)</code></p>

      <h2>When to pay the interest</h2>
      <p>You have two legal options under the RTA:</p>
      <ol>
        <li><strong>Pay annually</strong> on the anniversary of the deposit. This is the safer practice — interest never gets large enough to surprise anyone.</li>
        <li><strong>Pay at the end of the tenancy</strong> as a lump sum together with the deposit return. Most small landlords choose this option.</li>
      </ol>
      <p>You may NOT keep the interest as part of your income. It belongs to the tenant.</p>

      <h2>What you must give the tenant</h2>
      <p>At move-out you must provide a written statement showing:</p>
      <ul>
        <li>The original deposit amount</li>
        <li>The rate(s) used for each year</li>
        <li>The interest accrued each year</li>
        <li>The total deposit plus interest payable</li>
        <li>Any deductions you are taking (with receipts and itemization)</li>
      </ul>
      <p>If you deduct anything from the deposit, you must do so within <strong>10 days</strong> of the tenancy ending. Miss the 10-day window and you may have to refund the full deposit plus interest regardless of damages.</p>

      <h2>Common landlord mistakes</h2>
      <ol>
        <li><strong>Forgetting to track the rate change.</strong> The rate can change every January 1. A 5-year tenancy might span 5 different rates.</li>
        <li><strong>Compounding wrong.</strong> The interest compounds annually, not monthly.</li>
        <li><strong>Forgetting the pet damage deposit.</strong> If a tenant paid a separate pet damage deposit (allowed in Alberta — max half a month's rent), interest applies to it too.</li>
        <li><strong>Keeping the interest as income.</strong> Not allowed. The interest is the tenant's money.</li>
        <li><strong>No paper trail.</strong> If you can't produce the interest calculation at RTDRS, the tribunal will simply award the tenant the maximum reasonable interest plus the deposit.</li>
      </ol>

      <h2>What happens if you skip it</h2>
      <p>You can be ordered to pay all back interest plus a penalty. RTDRS arbitrators routinely award tenants the maximum reasonable interest figure when landlords cannot produce the math. Worse — repeat offenders can face administrative penalties from Service Alberta.</p>

      <h2>Official sources</h2>
      <p>The legal authority is the Alberta <em>Residential Tenancies Act</em>, section 44, and the <em>Security Deposit Interest Rate Regulation</em>. The current rate is always posted on the Service Alberta consumer information website.</p>

      <h2>Bottom line</h2>
      <p>It's two minutes of math per tenant per year — and the alternative is being ordered to pay everything anyway, plus damages. Set a calendar reminder for January 1 of every year to check the new rate, and either update your spreadsheet or let property management software calculate it automatically.</p>
    </>
  );
}

// ─── Post 3: N4 Ontario ────────────────────────────────────────────────────
function N4Post() {
  return (
    <>
      <p>An <strong>N4 — Notice to End your Tenancy Early for Non-payment of Rent</strong> is the first formal step Ontario landlords must take before they can apply to the Landlord and Tenant Board (LTB) to evict a tenant for unpaid rent. It looks like a simple form. It is not.</p>
      <p>The LTB throws out hundreds of N4 applications every year — not because the rent isn't actually owed, but because the landlord wrote the wrong number on a single line, used the wrong date format, or served the notice incorrectly. This post walks you through filling out an N4 correctly so your application actually gets heard.</p>

      <h2>When can you serve an N4?</h2>
      <p>You can serve an N4 the day after rent is missed. If rent is due on the 1st of the month and unpaid on the 2nd, you may serve N4 on the 2nd. There is no grace period in the Ontario <em>Residential Tenancies Act</em> (RTA), although many landlords voluntarily wait a few days as a courtesy.</p>

      <h2>The termination date — the most common mistake</h2>
      <p>You must give the tenant a minimum number of days to pay or move out before the termination date listed on the N4:</p>
      <ul>
        <li><strong>14 days</strong> for monthly, weekly, or daily tenancies</li>
        <li><strong>7 days</strong> for daily or weekly tenancies (rare in residential)</li>
      </ul>
      <p>The 14 days do <em>not</em> include the day of service. If you serve on March 1, the earliest termination date is March 15.</p>
      <p>If you write a termination date less than 14 days from service, the LTB will dismiss your eventual L1 application and you have to start over — losing another month of rent.</p>

      <h2>The seven sections of the N4 — line by line</h2>

      <h3>1. To: (Tenant name)</h3>
      <p>Use the legal name of every adult tenant on the lease, exactly as it appears on the lease. Misspellings are grounds for dismissal. If there are multiple tenants, list all of them.</p>

      <h3>2. Rental address</h3>
      <p>Full address including unit number. "123 Main St, Unit 4B, Toronto, ON, M5V 2K7" — not "123 Main".</p>

      <h3>3. Termination date</h3>
      <p>The date the tenant must move out IF they do not pay. Count carefully (see above). Use the format month/day/year as it appears on the official form.</p>

      <h3>4. Reasons</h3>
      <p>For an N4, the reason is always "you have not paid the rent that you owe." The form pre-fills this — do not edit it.</p>

      <h3>5. Rent breakdown table — this is where applications die</h3>
      <p>You must list each rental period with:</p>
      <ul>
        <li>Rental period (e.g., "March 1, 2026 to March 31, 2026")</li>
        <li>Rent due for that period</li>
        <li>Rent paid for that period</li>
        <li>Rent owing for that period</li>
      </ul>
      <p>Common errors that get the application tossed:</p>
      <ul>
        <li>Listing the wrong rent amount (e.g., post-rent-increase amount before the increase took legal effect)</li>
        <li>Including non-rent charges like NSF fees in the rent total (these are recoverable separately, not via N4)</li>
        <li>Including last month's rent deposit math incorrectly</li>
        <li>Listing rental periods out of order</li>
      </ul>

      <h3>6. Total rent owing</h3>
      <p>The sum of the "rent owing" column. Must match exactly. Off by one cent = potential dismissal.</p>

      <h3>7. Signature and date</h3>
      <p>Sign with the same name you used as landlord on the lease. If the property is owned by a numbered company, sign as an authorized officer.</p>

      <h2>How to serve the N4 legally</h2>
      <p>Under the RTA, you can serve a notice in any of these ways:</p>
      <ul>
        <li>Hand-delivery to the tenant</li>
        <li>Leaving it in the tenant's mailbox at the rental unit</li>
        <li>Sliding it under the door of the unit</li>
        <li>By mail to the address of the rental unit (add 5 days for deemed receipt)</li>
        <li>By email — only if the tenant has agreed in writing to be served by email</li>
      </ul>
      <p>Document the date and method of service. The LTB will ask. A photo of the notice slid under the door with a timestamped phone clock is good evidence.</p>

      <h2>What happens after you serve the N4</h2>
      <p>The tenant has 14 days from service to either:</p>
      <ul>
        <li>Pay the full amount listed on the N4 (the notice is then void)</li>
        <li>Move out</li>
        <li>Do neither — in which case you can apply to the LTB on Form L1</li>
      </ul>
      <p>If the tenant pays in full within 14 days, the N4 is automatically void and you cannot apply for eviction based on that N4. If they pay partial, the N4 is still alive — but list the partial payment carefully on your L1.</p>

      <h2>The L1 application</h2>
      <p>If the tenant has not paid by day 15, file Form L1 with the LTB. Filing fee is currently $186. Bring the L1 hearing every receipt, the N4, your service evidence, and the lease.</p>

      <h2>The 7 most common N4 errors that get applications dismissed</h2>
      <ol>
        <li>Termination date less than 14 days from service</li>
        <li>Wrong rent amount listed (e.g., already increased)</li>
        <li>Including non-rent charges (NSF fees, parking, late fees) as "rent"</li>
        <li>Misspelled tenant name</li>
        <li>Missing unit number on address</li>
        <li>Wrong service method or undocumented service</li>
        <li>Tenant paid in full within 14 days but landlord still files L1</li>
      </ol>

      <h2>One small win: software-generated N4s</h2>
      <p>Modern property management platforms (including Central Rentals) auto-fill the N4 with the correct rent, dates, and rental periods directly from your rent ledger. The arithmetic mistake — the single biggest cause of LTB dismissals — disappears.</p>

      <h2>Bottom line</h2>
      <p>An N4 is the cheapest, fastest legal tool an Ontario landlord has — but only if it is filled out perfectly. Count to 14 carefully, get the rent math exactly right, document service, and never include non-rent charges. Do those four things and your L1 will get heard on the merits.</p>
    </>
  );
}

function KijijiVsCentralRentalsPost() {
  return (
    <>
      <p><strong>Quick answer:</strong> Kijiji is great for getting eyeballs on a listing. Central Rentals Canada is the platform you use <em>after</em> someone calls — to screen, sign, collect rent, file your T776, and never repeat the &quot;tenant ghosted me on month 2&quot; story. They aren&apos;t really competitors. They&apos;re different stages of the same workflow.</p>
      <p>That said, plenty of Canadian landlords post on Kijiji and then try to manage the rest with Gmail, e-transfers, and a Word doc lease. This post compares <strong>Kijiji + manual management</strong> versus <strong>Central Rentals Canada end-to-end</strong>, so you know exactly what you&apos;re trading away.</p>

      <h2>What each one actually does</h2>
      <p><strong>Kijiji Real Estate</strong> is a free classifieds listing. You post a photo, a description, your phone number, and your rent. Renters call or email. That&apos;s the whole product. You&apos;re responsible for everything else — credit checks, lease, deposits, rent collection, maintenance, taxes — using whatever tools you already have.</p>
      <p><strong>Central Rentals Canada</strong> is a Canadian-built property management platform. You list properties, accept applications, screen credit + reference + identity, e-sign provincially-compliant leases, help landlords track rent (tenants pay them directly via e-Transfer, cheque, or bank transfer), track maintenance tickets, log expenses, and export a CRA-ready T776 worksheet at year-end. Listings auto-syndicate to Kijiji, Facebook Marketplace, and the Central Rentals public directory.</p>

      <h2>Side-by-side</h2>
      <p>Here&apos;s what you trade off in a typical small-landlord workflow:</p>
      <table>
        <thead>
          <tr><th>Step</th><th>Kijiji + manual</th><th>Central Rentals Canada</th></tr>
        </thead>
        <tbody>
          <tr><td>List a property</td><td>Free; type each field manually</td><td>Free; auto-syndicates to Kijiji + FB Marketplace</td></tr>
          <tr><td>Accept applications</td><td>Email + screenshots</td><td>Branded application form with credit + reference checks built-in</td></tr>
          <tr><td>Screen tenant</td><td>Buy a $30 credit report yourself</td><td>Included in paid plans; Equifax/TransUnion pull</td></tr>
          <tr><td>Sign a lease</td><td>Word doc you maintain</td><td>Province-specific (RTA / RTB / OLT) e-sign templates</td></tr>
          <tr><td>Collect deposits</td><td>Cash, cheque, or e-transfer to your personal account</td><td>Stripe + Flinks; held separately, refundable on move-out</td></tr>
          <tr><td>Collect monthly rent</td><td>Chase e-transfers, manually reconcile</td><td>Auto-pay; bank-link reconciles deposits automatically</td></tr>
          <tr><td>Handle maintenance</td><td>SMS thread on your phone</td><td>Tickets, vendor directory, photos uploaded from tenant&apos;s phone</td></tr>
          <tr><td>File your CRA T776</td><td>Spreadsheet in March</td><td>One-click export; expenses categorized by CRA line</td></tr>
          <tr><td>Late rent / N4 / 14-day notice</td><td>Print, fill by hand, hope the math&apos;s right</td><td>Auto-filled from your rent ledger</td></tr>
        </tbody>
      </table>

      <h2>Where Kijiji absolutely wins</h2>
      <p>It&apos;s free. It&apos;s the most-used classifieds site in Canada. Boost a listing for $5 and you&apos;ll get 5-10x the eyeballs vs the un-boosted version. For a single-unit landlord posting once a year, that&apos;s perfect.</p>
      <p>And Kijiji is mobile-optimized for renters. The search experience for a 22-year-old looking for their first 1BR is honestly better than most province-specific rental sites. We integrate <em>with</em> Kijiji rather than against it, exactly because that&apos;s where the demand is.</p>

      <h2>Where Kijiji + manual breaks down</h2>
      <p>Three pain points hit every landlord, usually in this order:</p>
      <ol>
        <li><strong>Screening</strong>. You meet a tenant who seems nice, you skip the credit check to save $30, three months later they stop paying. The credit report would have shown 4 collections.</li>
        <li><strong>Rent collection</strong>. E-transfer limits, NSF e-transfers, tenants who &quot;forgot,&quot; weekend deposits that don&apos;t hit your account until Monday. By year 3 you have a spreadsheet with red cells.</li>
        <li><strong>Taxes</strong>. The CRA T776 doesn&apos;t care about your good intentions. If you can&apos;t produce a receipt for that &quot;$1,400 plumber&quot; on Line 8960, that deduction is gone. Most small landlords lose 10-25% of their legitimate deductions every year just because the paperwork was never logged.</li>
      </ol>

      <h2>The honest pricing answer</h2>
      <p>Kijiji costs $0 (free listing) to ~$5 per boost. A typical landlord boosts a listing 1-3 times per vacancy.</p>
      <p>Central Rentals Canada has a free tier (up to 2 units, basic features) and paid plans starting at <strong>$29 CAD/month</strong> per unit for full screening, e-sign, auto-rent-collection, T776 export, and the Smart Operator assistant. For a $1,800/month rental, $29 buys you 1.6% of your rent — and the bank-link reconciliation alone saves most landlords ~3 hours a month.</p>

      <h2>Who should stay on Kijiji + manual</h2>
      <p>Honestly — if you own one property, have a long-term tenant who pays on time, and have a great relationship with them, you don&apos;t need software. List on Kijiji once every 5 years when they leave, and keep doing what you&apos;re doing.</p>

      <h2>Who should switch to Central Rentals</h2>
      <ul>
        <li>You own <strong>2+ doors</strong> (the math flips at 2)</li>
        <li>You&apos;ve had <strong>any tenant ghost, miss rent, or fight you on the security deposit</strong> in the last 3 years</li>
        <li>You&apos;ve ever <strong>missed a CRA deduction</strong> because the receipt was on a different phone</li>
        <li>You&apos;re <strong>scaling</strong> — adding a unit per year — and the spreadsheet is starting to scare you</li>
        <li>You want <strong>rent insurance</strong> or to <strong>finance new doors</strong> — banks now ask for rent-collection history that&apos;s machine-readable</li>
      </ul>

      <h2>How we use Kijiji ourselves</h2>
      <p>Central Rentals auto-posts your listings to Kijiji (and Facebook Marketplace, and a few province-specific sites) the moment you mark a unit vacant. We don&apos;t replace Kijiji as a demand-generation channel — we just turn the leads you get there into signed leases and collected rent without you having to copy/paste anything.</p>

      <h2>Bottom line</h2>
      <p>Kijiji is the loudspeaker. Central Rentals is the rest of the building. Most landlords need both, and the value of the &quot;rest&quot; compounds the second you get to 2+ doors or you have to file your first T776 with rental income on it.</p>
      <p>If you&apos;re a single-door landlord with a great long-term tenant, you don&apos;t need us. Anyone else — give the free tier a try, list a property, and see if the bank-link reconciliation alone doesn&apos;t change your weekend.</p>
    </>
  );
}


function BestPmsCanadaPost() {
  return (
    <>
      <p><strong>Quick answer:</strong> the best property management software for Canadian landlords in 2026 is <strong>Central Rentals Canada</strong>, followed by SingleKey (screening-heavy), Rent Panda (Ontario-only), landlordy (indie mobile), Buildium (US import), TenantCloud (mid-market), and Yardi Breeze (5+ units). Below, we scored each against the eight things a Canadian landlord actually needs: T776 export, RTA/RTB/OLT-compliant leases, e-Transfer + Rent tracking, French (Loi 41) support, mobile-first tenant portal, expenses/tax logging, screening (Certn/Equifax), and honest CAD pricing.</p>

      <h2>What "Canadian" actually means in property management software</h2>
      <p>Most listicles rank software built for US landlords and shove Canadian users into an American workflow. That breaks the second you file a T776, need a Loi 41 bilingual lease in Quebec, or try to reconcile an Interac e-Transfer that showed up in a personal name.</p>
      <p>The scoring here is deliberately Canadian-first. If a platform can&apos;t export a T776 or accept e-Transfer without a chargeback, it drops.</p>

      <h2>The 2026 ranking</h2>

      <h3>1. Central Rentals Canada — best overall for 1–100 units</h3>
      <p>Purpose-built for Canadian landlords. Auto-generated province-specific leases (RTA in ON, RTB in BC, OLT/CTAL in QC, RTDRS in AB). One-click T776 export with expenses categorized by CRA line. Rent tracking in CAD (peer-to-peer), e-Transfer inbox reconciliation, and Flinks bank-link for tenants who prefer PAD. Bilingual (en-CA / fr-CA) throughout. Free tier up to 2 units; paid plans from <strong>$35 CAD/mo flat</strong>. Best for anyone who wants a full workflow without a US accent.</p>

      <h3>2. SingleKey — best for tenant screening only</h3>
      <p>Canadian screening tool with Equifax + criminal + eviction history. Not a full PMS — you still need something else for lease + rent collection. Reports from ~$25/pull. Pair with Central Rentals if you already have a lease workflow you love.</p>

      <h3>3. Rent Panda — best if you only own in Ontario</h3>
      <p>Ontario-focused. Solid N4 / L1 templates, integrated with the LTB. No T776 export, no French support, weak outside Ontario. From $19/mo.</p>

      <h3>4. landlordy — best indie mobile-only</h3>
      <p>A polished iOS/Android app for tracking one to five doors on your phone. No screening, no e-sign, no online rent collection. Great if you literally just want a rent ledger. One-time purchase, no cloud sync.</p>

      <h3>5. Buildium — best for professional PMs</h3>
      <p>US-built. Excellent property manager UX. In Canada you lose: T776 export, native e-Transfer, French lease, RTA/RTB templates. From ~US $70/mo. Overkill for &lt; 15 doors.</p>

      <h3>6. TenantCloud — best mid-market alternative</h3>
      <p>US SaaS with some Canadian-friendly options. From US $17/mo. Rent collection is US-first — CAD works but reconciliation is manual.</p>

      <h3>7. Yardi Breeze — best if you&apos;re already in the Yardi ecosystem</h3>
      <p>Solid property management for 5+ door portfolios. Expensive relative to the Canadian competitors. From ~$100/mo per property. Great reports, weak on modern tenant UX.</p>

      <h2>Feature-by-feature scorecard</h2>
      <table>
        <thead>
          <tr><th>Platform</th><th>T776 export</th><th>Province leases</th><th>e-Transfer</th><th>French</th><th>Starting price CAD</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Central Rentals Canada</strong></td><td>✅ 1-click</td><td>✅ All 13</td><td>✅ Auto-reconcile</td><td>✅ fr-CA</td><td><strong>$35/mo</strong></td></tr>
          <tr><td>SingleKey</td><td>❌</td><td>❌</td><td>❌</td><td>❌</td><td>Per report</td></tr>
          <tr><td>Rent Panda</td><td>❌</td><td>ON only</td><td>Manual</td><td>❌</td><td>$19/mo</td></tr>
          <tr><td>landlordy</td><td>❌</td><td>❌</td><td>❌</td><td>❌</td><td>One-time</td></tr>
          <tr><td>Buildium</td><td>❌</td><td>❌</td><td>❌</td><td>❌</td><td>~$95/mo</td></tr>
          <tr><td>TenantCloud</td><td>❌</td><td>❌</td><td>Manual</td><td>❌</td><td>~$23/mo</td></tr>
          <tr><td>Yardi Breeze</td><td>❌</td><td>❌</td><td>Manual</td><td>❌</td><td>~$130/mo</td></tr>
        </tbody>
      </table>

      <h2>How to pick in under 60 seconds</h2>
      <ul>
        <li><strong>Under 3 units, want it free</strong> → Central Rentals free tier</li>
        <li><strong>Just want a great mobile ledger</strong> → landlordy</li>
        <li><strong>Ontario-only, need N4/L1</strong> → Central Rentals (all-province) or Rent Panda</li>
        <li><strong>Quebec, need Loi 41 bilingual</strong> → Central Rentals (only option)</li>
        <li><strong>1–20 units, want everything in one place</strong> → Central Rentals</li>
        <li><strong>20+ units, US HQ, integrated with QuickBooks</strong> → Buildium or Yardi Breeze</li>
      </ul>

      <h2>Bottom line</h2>
      <p>If you rent in Canada and file a T776, start with Central Rentals Canada&apos;s free tier — the 90-second setup gets you a live listing, a compliant lease, and a rent-collection link. Upgrade only when you hit 3+ doors or want the automated screening. Everything else on this list is either narrower, US-first, or built for portfolios of 20+.</p>
    </>
  );
}


function CheapestPmsPost() {
  return (
    <>
      <p><strong>Quick answer:</strong> the cheapest property management software in Canada for landlords under 10 units is <strong>Central Rentals Canada</strong> — the free tier covers up to 2 units with full listing, lease e-sign, and rent collection. If you need to scale, paid tiers start at <strong>$35 CAD/month flat</strong> (not per unit), with Klarna Pay-in-4 available.</p>

      <h2>What "cheapest" actually means for a Canadian landlord</h2>
      <p>US listicles love to quote &quot;$0 to start&quot; software and bury a $70/mo minimum in the fine print. In Canada the real questions are:</p>
      <ol>
        <li>Does it charge per unit, per property, or flat?</li>
        <li>Does the &quot;free&quot; tier actually let you collect rent, or just list?</li>
        <li>What does rent collection cost per transaction (Stripe fees, ACH fees, e-Transfer)?</li>
        <li>Does it work in <em>my</em> province (Loi 41 in QC, RTA in ON, RTB in BC)?</li>
      </ol>

      <h2>Ranked by real total cost of ownership</h2>

      <h3>1. Central Rentals Canada — free up to 2 units, then $35 CAD/mo flat</h3>
      <p>Free tier: unlimited listings, one lease, peer-to-peer rent tracking (Central Rentals never touches rent money). Paid tier: everything unlocked, still <strong>flat monthly</strong> (not per unit). For a 4-door landlord that&apos;s $15/door/mo — the lowest per-door price in Canada that includes T776 export.</p>

      <h3>2. landlordy — one-time app purchase, no cloud</h3>
      <p>Free tier limits you to 3 units and blocks reports. Paid version is ~$15 one-time. Zero cloud sync, zero rent collection, zero screening. Cheap for a reason.</p>

      <h3>3. Rent Panda — $19/mo Ontario-only</h3>
      <p>Cheap if you own only in Ontario. Per-unit surcharges hit fast once you have 3+ units. No T776.</p>

      <h3>4. TenantCloud — from ~$17 USD/mo (~$23 CAD)</h3>
      <p>US-priced. Currency drift eats the discount. Free tier is available but blocks CAD rent collection.</p>

      <h3>5. Cozy → Apartments.com — free but discontinued in Canada</h3>
      <p>Redirects to Apartments.com which is US-only. Skip.</p>

      <h2>The hidden cost most calculators miss</h2>
      <p>Payment processing. Whatever platform you pick, Central Rentals does not process rent. Landlords receive rent directly from tenants (Interac e-Transfer, cheque, or bank transfer) — 0% platform fee because we never touch the money. Some competitors add 0.5–1.5% on top, which on a $2,000 rent is $10–30/month extra.</p>

      <h2>The Klarna angle</h2>
      <p>Central Rentals accepts <strong>Klarna Pay-in-4</strong> on the subscription itself, so a $35/mo Starter plan is billed as 4 payments of $15 — helpful if your rental cash-flow is uneven while a unit turns over.</p>

      <h2>Bottom line</h2>
      <p>For 1–2 units, the Central Rentals free tier is genuinely free — no card required, and no markup on rent collection. For 3+ units the $35/mo flat plan beats every per-unit competitor. Only pick landlordy if you literally never want cloud sync.</p>
    </>
  );
}


function AlbertaSmallLandlordPost() {
  return (
    <>
      <p><strong>Quick answer:</strong> if you own <strong>1 to 5 rental units in Alberta</strong>, you don&apos;t need Buildium or AppFolio. This guide walks through everything you actually need — registering as a landlord, collecting rent legally, handling security deposits, and filing T776 — plus the free templates that make each step take five minutes instead of five hours.</p>

      <h2>Do I need to register as a landlord in Alberta?</h2>
      <p>Alberta does <em>not</em> require a provincial landlord licence for residential rentals. Cities do have their own rules — <strong>Calgary requires a business licence</strong> for short-term rentals and multi-suite operators, and <strong>Edmonton requires one for anything beyond one accessory suite</strong>. If you rent 1–5 long-term units in your own name, you&apos;re good with just a business number for CRA (free, 5 minutes online).</p>
      <p>What you <em>do</em> need before your first tenant moves in:</p>
      <ul>
        <li>A Residential Tenancy Agreement (Alberta&apos;s <em>Residential Tenancies Act</em> is the governing law).</li>
        <li>A separate trust account for security deposits (interest-bearing — see next section).</li>
        <li>Insurance that covers loss of rental income (standard homeowner insurance does not).</li>
      </ul>

      <h2>How much can I charge for a security deposit?</h2>
      <p>Alberta caps the security deposit at <strong>one month&apos;s rent</strong>. It must be held in a trust account and earn the annual rate posted by Service Alberta. The 2026 rate is 1.6 % (Service Alberta re-publishes each January 1). You owe the tenant that interest either annually or on move-out.</p>
      <p>Handy math: on a $1,800 deposit at 1.6 %, the interest for one year is <strong>$28.80</strong>. Skipping it opens you to a claim at the Residential Tenancy Dispute Resolution Service (RTDRS) — a $75 filing fee for the tenant, up to $50,000 in judgments against you.</p>

      <h2>How does rent collection work in Alberta?</h2>
      <p>Alberta has no rent-control cap — you can raise rent by any amount, but only <strong>once every 365 days per tenancy</strong> and only with 90 days&apos; written notice. You must also honour the rent stated in your signed lease until it renews.</p>
      <p>Accepted payment methods, ranked by landlord risk:</p>
      <ol>
        <li><strong>Interac e-Transfer</strong> — instant, no chargebacks, free for you and the tenant. This is what 82 % of Alberta small landlords use in 2026.</li>
        <li><strong>Pre-authorized debit</strong> — hands-off but takes 3 business days to settle. Best for reliable long-term tenants.</li>
        <li><strong>Credit card</strong> — 2.9 % + $0.30 fee per transaction. Only worth it if you pass the fee to the tenant.</li>
        <li><strong>Cash</strong> — legal but forces you to issue a signed receipt every time. Prefer to avoid.</li>
        <li><strong>Cheques</strong> — NSF risk. NSF in Alberta lets you serve a 14-day notice for non-payment (RTA s. 30).</li>
      </ol>

      <h2>Handling non-payment: the Alberta process</h2>
      <p>Alberta is one of the fastest provinces to remove a non-paying tenant — <strong>RTDRS averages 2–4 weeks</strong> vs. Ontario&apos;s LTB (6+ months). The path:</p>
      <ol>
        <li>Rent is late → serve a <strong>14-day notice for substantial breach</strong> (RTA s. 30) the day rent is missed.</li>
        <li>Day 15, if unpaid → file at RTDRS ($75) or Provincial Court ($100).</li>
        <li>Hearing happens 10–15 days later, usually by phone.</li>
        <li>Writ of possession → Sheriff enforces move-out.</li>
      </ol>

      <h2>Filing T776 for your Alberta rental</h2>
      <p>Every Canadian landlord — including you — files a T776 with their personal tax return. Alberta doesn&apos;t add anything extra to the form, but three lines matter more here than in other provinces:</p>
      <ul>
        <li><strong>Line 8710 — Interest</strong>. Alberta&apos;s aggressive mortgage market means this is often your biggest deduction.</li>
        <li><strong>Line 8960 — Repairs</strong>. Alberta&apos;s temperature swings mean HVAC and roof repairs are frequent — keep every receipt.</li>
        <li><strong>Line 9270 — Other expenses</strong>. Snow removal and Chinook wind damage go here.</li>
      </ul>
      <p>Central Rentals categorises every payment into the right T776 line as you go, so March takes about 15 minutes instead of a shoebox.</p>

      <h2>Should I use Buildium, AppFolio, or Central Rentals for 1–5 units?</h2>
      <p>Short version: neither Buildium nor AppFolio is built for you. Buildium&apos;s minimum is ~$95 CAD/month after FX and doesn&apos;t know what an RTDRS notice is. AppFolio starts at $280 USD/month. Central Rentals is <strong>$35 CAD/month flat for 1–10 units</strong>, includes Alberta-specific notice templates, and one-click T776 export.</p>
      <p><Link to="/alberta-small-landlord-software" className="text-emerald-700 font-bold hover:underline">See the Alberta small-landlord page</Link> for a full feature-by-feature comparison.</p>

      <h2>The five-tool starter kit</h2>
      <ol>
        <li><Link to="/tools/roi-calculator" className="text-emerald-700 font-bold hover:underline">ROI calculator</Link> — plug in your Alberta numbers before you buy.</li>
        <li><Link to="/features/provincial-lease-generator" className="text-emerald-700 font-bold hover:underline">Alberta lease generator</Link> — RTA-compliant, e-signable in 90 seconds.</li>
        <li><Link to="/features/tenant-screening-canada" className="text-emerald-700 font-bold hover:underline">Tenant screening</Link> — Certn credit + background, PIPEDA-compliant.</li>
        <li><Link to="/features/rent-collection-canada" className="text-emerald-700 font-bold hover:underline">Rent collection</Link> — Interac e-Transfer auto-reconciliation.</li>
        <li><Link to="/features/t776-tax-export-software" className="text-emerald-700 font-bold hover:underline">T776 export</Link> — hand your accountant a clean spreadsheet in one click.</li>
      </ol>

      <h2>Bottom line</h2>
      <p>Alberta&apos;s landlord rules are, on balance, the most owner-friendly in Canada — no rent cap, fastest dispute resolution, no provincial licence for small operators. The catch is that all the friction comes at tax time and when a tenant stops paying. Both are exactly the two moments Central Rentals is built for — start with the <Link to="/signup?role=landlord&province=AB" className="text-emerald-700 font-bold hover:underline">free landlord account</Link> and you&apos;re set up in about a minute.</p>
    </>
  );
}



function BuildiumAlternativesPost() {
  return (
    <>
      <p><strong>Quick answer:</strong> the best Buildium alternative for Canadian landlords is <strong>Central Rentals Canada</strong> — same feature depth (listings, leases, rent collection, maintenance, tax reporting), but Canadian-first (T776, RTA, RTB, Loi 41, e-Transfer, French UI). Below are six other alternatives worth knowing, ranked by fit.</p>

      <h2>Why Canadian landlords look for a Buildium alternative</h2>
      <ul>
        <li><strong>Currency and pricing</strong>: Buildium is US $70+/mo, which after FX is ~$95 CAD.</li>
        <li><strong>No T776 export</strong>. You end up rebuilding the schedule of expenses manually every March.</li>
        <li><strong>No Interac e-Transfer</strong>. Buildium supports US ACH only — for Canadian rent, this is a dealbreaker.</li>
        <li><strong>No bilingual UI</strong>. If you rent in Quebec, Loi 41 requires the tenant-facing bail in French. Buildium can&apos;t generate one.</li>
        <li><strong>Overkill for small portfolios</strong>. Under 15 units, Buildium&apos;s advanced accounting is unused.</li>
      </ul>

      <h2>The 7 alternatives (2026)</h2>

      <h3>1. Central Rentals Canada — closest Buildium replacement for &lt; 50 units</h3>
      <p>Full-stack Canadian PMS. Where Buildium wins on GL depth, Central Rentals wins on Canadian compliance + honest CAD pricing (from $35/mo flat, not per unit). Includes T776 export, RTA/RTB/OLT/CTAL leases, e-Transfer reconciliation, Certn screening, peer-to-peer rent tracking, bilingual UI. Best drop-in for landlords with 1–50 doors.</p>

      <h3>2. AppFolio — for 50+ door PMs who need mobile-first</h3>
      <p>US-built but has a Canadian region. Great mobile UX. Expensive: ~$1.40 USD/unit/mo with a $280/mo minimum. Only worth it above ~150 units.</p>

      <h3>3. Yardi Breeze — for 5–50 doors who want Yardi Excel exports</h3>
      <p>Cheaper than Buildium. Works in Canada. Weaker on modern tenant UX.</p>

      <h3>4. TenantCloud — for landlords who want a familiar UI at half Buildium&apos;s price</h3>
      <p>US SaaS. ~$23 CAD/mo. Rent collection is US-first — Canadian rent goes through manual reconciliation.</p>

      <h3>5. DoorLoop — for landlords who want the newest US entrant</h3>
      <p>Modern UI, US-only rent collection. Cannot generate a Canadian lease.</p>

      <h3>6. Propertyware — enterprise-only</h3>
      <p>Realpage&apos;s enterprise product. Overkill unless you have 500+ doors and a full accounting team.</p>

      <h3>7. Excel + Stripe Payment Links — the DIY option</h3>
      <p>Legitimately what most 1–2 door landlords still use. Free but you lose screening, provincial compliance, and any hope of a fast T776 in March.</p>

      <h2>Head-to-head: Central Rentals Canada vs Buildium</h2>
      <table>
        <thead>
          <tr><th>Feature</th><th>Central Rentals Canada</th><th>Buildium</th></tr>
        </thead>
        <tbody>
          <tr><td>Pricing (CAD)</td><td><strong>$35/mo flat</strong></td><td>~$95/mo minimum</td></tr>
          <tr><td>T776 export</td><td>✅ 1-click</td><td>❌</td></tr>
          <tr><td>Province leases (all 13)</td><td>✅</td><td>❌</td></tr>
          <tr><td>Interac e-Transfer</td><td>✅ Auto-reconcile</td><td>❌ US ACH only</td></tr>
          <tr><td>Bilingual (fr-CA)</td><td>✅</td><td>❌</td></tr>
          <tr><td>Subscription monthly billing</td><td>✅</td><td>❌</td></tr>
          <tr><td>GL depth</td><td>Solid for &lt; 50 doors</td><td>Enterprise-grade</td></tr>
        </tbody>
      </table>

      <h2>Bottom line</h2>
      <p>If you have fewer than 50 doors and you rent in Canada, Central Rentals Canada replaces Buildium end-to-end at ~60 % of the price and adds the Canadian primitives Buildium never shipped. Above 50 doors, Buildium or AppFolio still make sense if you have a full accounting workflow — but everyone else, start with Central Rentals&apos; free tier and only upgrade when the second door lands.</p>
    </>
  );
}



function RentIncrease2026Post() {
  return (
    <>
      <p><strong>Quick answer:</strong> in 2026, rent-control caps range from <strong>0%</strong> (BC pause) to <strong>uncapped</strong> (Alberta, Saskatchewan). Below is the province-by-province breakdown — the exact cap, the notice required, and the one form you have to serve to make the raise legal.</p>

      <h2>How rent-increase caps work in Canada</h2>
      <p>Every province sets its own rent-increase cap through its Residential Tenancies Act. Three rules apply everywhere:</p>
      <ul>
        <li>You can only raise rent <strong>once every 12 months</strong> per tenancy.</li>
        <li>You must give written notice — <strong>usually 3 months in advance</strong>, longer in Quebec and BC.</li>
        <li>The correct provincial form is mandatory — a text message or email is not enough to make the increase legal.</li>
      </ul>

      <h2>2026 rent-increase caps by province</h2>
      <table>
        <thead>
          <tr><th>Province</th><th>2026 cap</th><th>Notice</th><th>Form</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>Alberta</strong></td><td>No cap</td><td>90 days written</td><td>Any written notice</td></tr>
          <tr><td><strong>British Columbia</strong></td><td>3.0% (RTB annual)</td><td>3 full calendar months</td><td>RTB-7</td></tr>
          <tr><td><strong>Manitoba</strong></td><td>3.0%</td><td>3 months written</td><td>Residential Tenancies Branch form</td></tr>
          <tr><td><strong>New Brunswick</strong></td><td>Tied to CPI (~2.5%)</td><td>6 months (yearly), 3 months (monthly)</td><td>Any written notice</td></tr>
          <tr><td><strong>Newfoundland &amp; Labrador</strong></td><td>No cap</td><td>8 weeks written</td><td>Any written notice</td></tr>
          <tr><td><strong>Nova Scotia</strong></td><td>5.0% (cap extended to Dec 31 2027)</td><td>4 months written</td><td>Form D</td></tr>
          <tr><td><strong>Ontario</strong></td><td>2.5%</td><td>90 days</td><td>N1 (above-cap = N2)</td></tr>
          <tr><td><strong>PEI</strong></td><td>3.0% (heated) / 0% (unheated)</td><td>3 months written</td><td>Form 1</td></tr>
          <tr><td><strong>Quebec</strong></td><td>TAL suggested rates by heat source</td><td>3–6 months (before renewal)</td><td>Avis de modification (TAL)</td></tr>
          <tr><td><strong>Saskatchewan</strong></td><td>No cap</td><td>6 months (yearly), 12 months from last increase</td><td>Any written notice</td></tr>
          <tr><td><strong>Yukon</strong></td><td>Tied to CPI</td><td>3 months written</td><td>Yukon RTO form</td></tr>
          <tr><td><strong>NWT</strong></td><td>No cap</td><td>90 days (yearly), 30 days (monthly)</td><td>Any written notice</td></tr>
          <tr><td><strong>Nunavut</strong></td><td>No cap</td><td>3 months written</td><td>Any written notice</td></tr>
        </tbody>
      </table>

      <h2>The two rules landlords miss most</h2>
      <ol>
        <li><strong>The clock resets on the last raise, not the lease anniversary.</strong> If you raised on Sep 1 2025, you cannot raise again until Sep 1 2026 — even if the tenant moved in on Feb 1.</li>
        <li><strong>Rate cards don&apos;t override lease terms.</strong> If your lease says rent is $1,800 for 12 months, you can&apos;t raise mid-lease no matter what your province allows.</li>
      </ol>

      <h2>Above-cap increases (Ontario N2, BC additional-rent)</h2>
      <p>Ontario and BC let you request an above-cap increase for extraordinary expenses (major capital repairs, tax hikes) using Form N2 (ON) or an application to the RTB (BC). Approval is not guaranteed — expect 4–8 months of paperwork and a hearing.</p>

      <h2>Common mistakes that cost landlords $$$</h2>
      <ul>
        <li>Serving notice by text — invalid in every province. Must be written on the provincial form or an equivalent document with the tenant&apos;s address, current rent, new rent, and effective date.</li>
        <li>Skipping the 12-month waiting period after a mid-lease adjustment.</li>
        <li>Raising by the CPI rate but forgetting the tenant needs the full notice period — a 2% raise served 60 days out is still invalid in Ontario.</li>
      </ul>

      <h2>How Central Rentals handles it</h2>
      <p>Pick your province, enter the new rent, and Central Rentals generates the right form (N1, RTB-7, Form D, Avis de modification), fills the tenant details, and schedules delivery so the notice lands the day it&apos;s legal — no paralegal, no LTB do-over. Free with every landlord account.</p>
      <p><Link to="/tools/rent-increase-notice" className="text-emerald-700 font-bold hover:underline">Generate a rent-increase notice</Link> · <Link to="/features/provincial-lease-generator" className="text-emerald-700 font-bold hover:underline">See provincial forms library</Link></p>

      <h2>Bottom line</h2>
      <p>If your province caps rent (ON, BC, MB, NS, PE, QC, YT, NB), raise every 12 months at the cap or lose that year forever. If your province doesn&apos;t cap (AB, SK, NL, NT, NU), raise to market — but still give the required written notice. Missing the paperwork is the single most common way small landlords lose an easy $1,200 – $3,000 a year.</p>
    </>
  );
}


function OntarioRentIncreasePost() {
  return (
    <>
      <p><strong>Quick answer:</strong> Ontario&apos;s 2026 rent-increase cap is <strong>2.5%</strong>. You can raise once per 12 months per tenancy, and only after serving <strong>Form N1</strong> with <strong>90 days&apos; written notice</strong>. Miss any of those three rules and the raise is void — the tenant gets to keep paying the old rent forever.</p>

      <h2>What is the 2026 Ontario rent-increase cap?</h2>
      <p>Ontario sets an annual guideline based on the Ontario Consumer Price Index. For the calendar year <strong>2026, the guideline is 2.5%</strong> — up from 2.5% in 2025 and 2.5% in 2024. This applies to <em>most</em> rented units built and occupied before November 15, 2018. Anything after that date is exempt from the cap (see below).</p>

      <h2>Do I have to give notice? Yes — and here&apos;s how</h2>
      <p>Serving notice correctly is where most Ontario landlords lose the raise. The three rules:</p>
      <ol>
        <li><strong>Form N1</strong> is mandatory. A text, an email, or even a signed letter doesn&apos;t count — the LTB requires N1 (or N2 for above-guideline).</li>
        <li><strong>90 days written notice</strong>, calculated from the day the tenant receives it (not the day you sign).</li>
        <li><strong>Once every 12 months</strong> per tenancy — the clock resets on the day of the last raise, not on the lease anniversary.</li>
      </ol>

      <h2>Which units are exempt from the 2.5% cap?</h2>
      <ul>
        <li>Units first rented after <strong>November 15, 2018</strong> (new-build exemption under Bill 108).</li>
        <li>Vacant units — you can list any new rent on a fresh tenancy.</li>
        <li>Non-profit and co-op housing (regulated separately).</li>
        <li>Some social housing and long-term care residences.</li>
      </ul>

      <h2>Above-guideline increases (Form N2 / AGI)</h2>
      <p>If you spent big on capital repairs, extraordinary utility increases, or a security service, you can apply for an <strong>Above-Guideline Increase (AGI)</strong>. Realistic timelines:</p>
      <ul>
        <li>File L5 with LTB → hearing scheduled 6–8 months later.</li>
        <li>Approval is granted for capital work that&apos;s a genuine improvement (not repair-and-maintain).</li>
        <li>Approved AGI is <em>on top</em> of the 2.5% guideline and can be spread over 3 years.</li>
      </ul>

      <h2>Common mistakes landlords make in Ontario</h2>
      <ul>
        <li>Serving N1 in an email attachment without a wet signature — the LTB will throw it out.</li>
        <li>Raising rent in a fixed-term lease renewal without waiting 12 months from the last raise.</li>
        <li>Backdating N1 to hit the 90-day window — the tenant&apos;s receipt date is what counts.</li>
        <li>Bundling above-guideline expenses into the next N1 without filing L5. That&apos;s illegal collection.</li>
      </ul>

      <h2>Rent-increase math for a $2,200/month Ontario rental (2026)</h2>
      <p>Cap: 2.5%. Old rent: $2,200. New rent: <strong>$2,200 × 1.025 = $2,255/month</strong>. Extra revenue over the year: <strong>$660</strong>.</p>
      <p>Now imagine you forgot to serve N1 on time. You lose $660 this year and the raise never happens for 2026 — you have to wait until 2027 to try again. That&apos;s the cost of a 20-second mistake.</p>

      <h2>How Central Rentals handles Ontario N1 for you</h2>
      <p>Inside the app you pick <em>Rent → New notice → Ontario</em>. Central Rentals auto-fills Form N1 with your unit, tenant, current rent, guideline-capped new rent, and the correct effective date (91+ days from send). E-sign delivery + audit log so you have proof of receipt for the LTB.</p>
      <p><Link to="/tools/rent-increase-notice" className="text-emerald-700 font-bold hover:underline">Generate a free Ontario N1 notice</Link> · <Link to="/features/provincial-lease-generator" className="text-emerald-700 font-bold hover:underline">See every provincial form we support</Link></p>

      <h2>FAQ</h2>
      <p><strong>Can I raise rent every year in Ontario?</strong><br/>Yes — once every 12 months, at or below the 2.5% cap, with 90 days&apos; written notice on Form N1.</p>
      <p><strong>Can a tenant refuse a legal rent increase?</strong><br/>No. If the notice is on Form N1, at or below the cap, and 90+ days out, the increase takes effect automatically. If the tenant refuses to pay the new amount, you can serve <Link to="/blog/how-to-serve-n4-non-payment-ontario-2026" className="text-emerald-700 font-bold hover:underline">Form N4 for non-payment</Link>.</p>
      <p><strong>What if the tenant moves out?</strong><br/>The cap doesn&apos;t follow the unit. Vacant units in Ontario can be listed at any rent — that&apos;s why some landlords wait for turnover before raising to market.</p>
      <p><strong>Where does the 2.5% number come from?</strong><br/>The Ontario government publishes the guideline each June for the following calendar year. The 2026 guideline of 2.5% was set on June 20, 2025.</p>

      <h2>Bottom line</h2>
      <p>Ontario rent-increase law rewards discipline: raise every 12 months at the cap, serve N1 correctly, keep the audit trail. Miss any one and you leave $660+/year on the table per unit. Central Rentals runs the whole flow — from notice to receipt — free with every landlord account.</p>
    </>
  );
}

// ─── Commercial-Electrical PM Contract Playbook (2026) ────────────────
// Companion inbound-SEO piece for /crm/commercial-electrical-contractors.
// Target keyword: "commercial electrical pm contract template".
function CommercialElectricalPmPost() {
  return (
    <>
      <p><strong>Quick answer:</strong> commercial electrical shops that renew ≥90% of their preventive-maintenance (PM) contracts do three things every calendar quarter — they price PM as a fixed monthly fee (not a callout rate), they auto-schedule inspections 7 days before the site expects them, and they send a signed renewal quote <em>60 days</em> before contract expiry, not 30. This playbook shows the exact template, the pricing math, and the renewal sequence used by shops running at 94%.</p>

      <h2>Why PM contracts are the highest-margin work in commercial electrical</h2>
      <p>A recurring quarterly emergency-light inspection at a 40,000 sq ft plaza takes one tech ~2 hours and bills at <strong>$450–$650</strong>. Do four of them per year across ten plazas and that&apos;s <strong>$18,000–$26,000/year of predictable revenue</strong> per property manager — before any change-order work the inspection generates. Compared to reactive call-outs, PM contracts:</p>
      <ul>
        <li>Bill <strong>3× higher gross margin</strong> because the labour is scheduled during regular hours, not paid at 1.5× overtime</li>
        <li>Convert to change-order work at <strong>~35%</strong> — a burned-out ballast found on inspection becomes a $2,400 relamp job with zero customer-acquisition cost</li>
        <li>De-risk your cash flow: property managers pay Net-30 on standing invoices vs Net-60/90 on one-off callouts</li>
      </ul>

      <h2>The 6-part PM contract template (steal this)</h2>
      <ol>
        <li><strong>Scope of work</strong> — list every panel, every emergency-light circuit, every generator, every fire-alarm relay, every EV charger. Reference the ESA installation permit number where it exists. Ambiguity here loses you disputes.</li>
        <li><strong>Frequency + response times</strong> — quarterly inspections (13-week interval), 4-hour emergency response, 24-hour non-emergency. Attach an SLA table.</li>
        <li><strong>Exclusions</strong> — what triggers a change order (new circuit installs, panel upgrades, code-mandated retrofits, damage from third-party trades). Without this you eat every "while you&apos;re here…" request.</li>
        <li><strong>Fixed monthly fee + escalator</strong> — $X/month with a CPI-linked annual escalator (currently ~2.9%). Never bill hourly on PM.</li>
        <li><strong>Compliance clause</strong> — you carry $5M CGL, valid WSIB clearance certificate, valid COR ticket, valid ESA licence at all times. Attach copies as Schedule A. This is what wins you the RFP.</li>
        <li><strong>Termination + renewal</strong> — 60-day termination notice (either party), auto-renewal for 12 months unless notice served. This <em>flips</em> renewal psychology in your favour.</li>
      </ol>

      <h2>Pricing formula for a PM contract</h2>
      <p>The number that wins bids and holds margin:</p>
      <p><code>Monthly fee = (Annual labour hours × loaded rate × 1.25 admin uplift) ÷ 12</code></p>
      <p>Example — a 40,000 sq ft plaza, 4 inspections/year, 2 techs × 2h per visit at a $95/h loaded rate:</p>
      <ul>
        <li>Annual labour hours: <strong>16h</strong> (2 techs × 2h × 4 visits)</li>
        <li>Labour cost: 16h × $95 × 1.25 admin = <strong>$1,900/year</strong></li>
        <li>Monthly fee: <strong>$158/month</strong></li>
      </ul>
      <p>Add $50–$50/month for consumables (bulbs, ballasts, fuses under $50 each) so you never have to write up a change order for a $9 tube.</p>

      <h2>The 60-day renewal sequence that hits 94%</h2>
      <p>Every dropped renewal we&apos;ve seen came from a shop that emailed a renewal quote <em>7–14 days</em> before expiry. By then the property manager has already fielded three cheaper cold-bids and is under pressure to switch. Run this sequence instead:</p>
      <ul>
        <li><strong>Day −60:</strong> Send a signed renewal quote with the previous year&apos;s inspection findings attached (this is your differentiator — bidders don&apos;t have it).</li>
        <li><strong>Day −45:</strong> Book a 15-minute Zoom or on-site walkthrough. Ask what changed at the property (new tenants, new panel loads, EV chargers coming). Adjust scope.</li>
        <li><strong>Day −30:</strong> Send a Loom video showing three specific issues you caught last year that would have been outages. Include a testimonial line from another PM client.</li>
        <li><strong>Day −14:</strong> Auto-renewal reminder. Reference the 60-day termination clause — if no notice served, contract auto-renews at agreed escalator.</li>
      </ul>

      <h2>WSIB, COR, ESA — the three certs that actually decide RFPs</h2>
      <p>A property manager awarding a $150K/year PM portfolio at a REIT can lose their job if their contractor lets any of these lapse. Attach copies to every contract:</p>
      <ul>
        <li><strong>WSIB Clearance Certificate</strong> — refresh every 90 days. Auto-download from the WSIB portal. If expired, the PM cannot pay your invoice.</li>
        <li><strong>COR (Certificate of Recognition)</strong> or <strong>SECOR</strong> — mandatory for most commercial GCs in Ontario/BC/AB. Audit cycle is 3 years but action items due quarterly.</li>
        <li><strong>ESA Master Licence</strong> — proof of Ontario ECRA/ESA Master Electrician status. Number on every invoice.</li>
      </ul>

      <h2>Managing this without a CRM (spoiler: painful)</h2>
      <p>Most 5-15 tech shops run PM contracts on a Google Sheet + Outlook calendar + a WhatsApp group. That works until:</p>
      <ul>
        <li>One tech takes vacation and their scheduled quarterly falls through</li>
        <li>A WSIB cert expires the day of a scheduled inspection and the PM refuses to let you on site</li>
        <li>Renewal quotes stay in draft in someone&apos;s Outlook Sent folder for three weeks</li>
      </ul>
      <p>The rule of thumb: you can run <em>up to 12 recurring PM contracts</em> on spreadsheets. Beyond that you lose one contract per year to admin error, and each lost contract is $18–26K of revenue.</p>

      <h2>How Central Rentals handles this end-to-end</h2>
      <p>Central Rentals&apos; <Link to="/crm/commercial-electrical-contractors" className="text-emerald-700 font-bold hover:underline">CRM for commercial electrical contractors</Link> was built around this exact playbook:</p>
      <ul>
        <li>Every building is its own file with panel schedule, PM contract PDF, past inspection findings, and next-visit auto-schedule</li>
        <li>Cert dashboard for WSIB, COR, ESA, CGL with 30-day expiry alerts</li>
        <li>Auto-generated renewal sequence at Day −60/−45/−30/−14 with template emails</li>
        <li>Stripe ACH Net-30 invoicing with 15/30/45-day auto-dunning</li>
        <li>Sub-contractor pool filterable by ESA class, service area, and 90-day availability</li>
      </ul>
      <p>Start at CA$35/month, cancel any time. <Link to="/get-started" className="text-emerald-700 font-bold hover:underline">Start your subscription</Link> or <Link to="/demo" className="text-emerald-700 font-bold hover:underline">book a 15-min walkthrough</Link> on one of your buildings.</p>

      <h2>FAQ</h2>
      <p><strong>What&apos;s a fair loaded rate for a commercial electrician in Canada in 2026?</strong><br/>$85–$110/h in the GTA/Vancouver, $70–$95/h in Calgary/Edmonton, $65–$85/h in Halifax/Winnipeg. Loaded includes truck, insurance, WSIB, and 12% admin overhead.</p>
      <p><strong>Should I sign a 1-year or 3-year PM contract?</strong><br/>3-year with CPI escalator. Property managers actually prefer this because it locks their capital-planning budget too. Add a mutual 60-day termination for either party.</p>
      <p><strong>Do I need COR to bid on REIT portfolios?</strong><br/>Almost always yes. The larger REITs (RioCan, First Capital, Choice Properties) filter contractors on COR/SECOR before an RFP is even sent.</p>
      <p><strong>How do I price emergency after-hours calls inside a PM contract?</strong><br/>Include one 4-hour after-hours call per quarter in the base fee. Everything above bills at 1.5× loaded rate with a 2-hour minimum, prior-authorized above $500.</p>

      <h2>Bottom line</h2>
      <p>Recurring PM contracts are the highest-margin, most-predictable revenue in commercial electrical. Run the 6-part template, price on the formula, execute the 60-day renewal sequence, and keep WSIB/COR/ESA current. Do that on spreadsheets and you cap around 12 buildings. Do it on a CRM built for the workflow and you scale to 60+ buildings per admin without adding staff.</p>
    </>
  );
}

